import { Clock, ChevronRight } from "lucide-react";
import type { Guide } from "../content/types";
import { inline, renderBlock } from "../content/render";
import { categoryById } from "../content/registry";
import { Screenshot } from "./Screenshot";
import { Seo } from "./Seo";
import { Link } from "../router/Router";

// One reusable layout for every guide. Content comes entirely from the Guide
// data — numbered steps, optional screenshots, related links.
export function GuideDocument({ guide }: { guide: Guide }) {
  const cat = categoryById(guide.category);
  const isStub = guide.status === "stub";
  const noindex = guide.status !== "published";

  return (
    <article className="hc-guide">
      <Seo
        title={guide.title}
        description={guide.summary}
        noindex={noindex}
        path={guide.route}
      />

      <nav className="hc-crumb" aria-label="Breadcrumb">
        <Link to="/">Help</Link>
        <ChevronRight size={13} aria-hidden="true" />
        {cat ? <Link to={`/${cat.id}`}>{cat.label}</Link> : null}
        <ChevronRight size={13} aria-hidden="true" />
        <span className="hc-crumb__current">{guide.title}</span>
      </nav>

      <header className="hc-guide__head">
        <h1 className="hc-guide__title">{guide.title}</h1>
        <p className="hc-guide__summary">{guide.summary}</p>
        <div className="hc-guide__meta">
          {guide.readingTime ? (
            <span className="hc-chip">
              <Clock size={13} aria-hidden="true" /> {guide.readingTime} min read
            </span>
          ) : null}
          {isStub ? <span className="hc-chip hc-chip--soon">Coming soon</span> : null}
        </div>
      </header>

      {/* Intro and prerequisites take the same `inline code` as step text. */}
      {guide.intro ? <p className="hc-guide__intro">{inline(guide.intro)}</p> : null}

      {guide.prerequisites && guide.prerequisites.length > 0 ? (
        <section className="hc-prereq">
          <h2 className="hc-prereq__title">Before you begin</h2>
          <ul className="hc-ul">
            {guide.prerequisites.map((p, i) => (
              <li key={i}>{inline(p)}</li>
            ))}
          </ul>
        </section>
      ) : null}

      {isStub ? (
        <div className="hc-note hc-note--info">
          <span className="hc-note__label">Note</span>
          <span>
            This guide is being written. Full step-by-step instructions with
            screenshots are on the way.
          </span>
        </div>
      ) : (
        <ol className="hc-steps">
          {guide.steps.map((step, i) => (
            <li key={i} className="hc-step">
              <div className="hc-step__num" aria-hidden="true">
                {i + 1}
              </div>
              <div className="hc-step__body">
                <h2 className="hc-step__title">{step.title}</h2>
                {step.blocks.map((b, j) => renderBlock(b, j))}
                {step.screenshot ? <Screenshot shot={step.screenshot} /> : null}
              </div>
            </li>
          ))}
        </ol>
      )}

      {guide.related && guide.related.length > 0 ? (
        <section className="hc-related">
          <h2 className="hc-related__title">Related guides</h2>
          <div className="hc-related__grid">
            {guide.related.map((r) => (
              <Link key={r.to} to={r.to} className="hc-related__card">
                {r.label}
                <ChevronRight size={16} aria-hidden="true" />
              </Link>
            ))}
          </div>
        </section>
      ) : null}
    </article>
  );
}
